import React, { useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import LoginScreen from "../screens/login-screen.tsx";
import AdminPanelScreen from "../screens/admin-panel-screen.tsx";
import { AppContext } from "../context/app-context.tsx";
import MoviesList from "../screens/movie/movies-list.tsx";
import MovieDetail from "../screens/movie/movie-detail.tsx";
import GenresScreen from "../screens/genre/genres-screen.tsx";
import CategoriesScreen from "../screens/category/categories-screen.tsx";
import CountriesScreen from "../screens/country/countries-screen.tsx";
import LanguagesScreen from "../screens/language/languages-screen.tsx";
import NetworksScreen from "../screens/network/networks-screen.tsx";
import ActorsScreen from "../screens/actor/actors-screen.tsx";
import DirectorsScreen from "../screens/director/directors-screen.tsx";
import CommentsScreen from "../screens/admin-pages/comments-screen.tsx";
import ReportsScreen from "../screens/report/reports-screen.tsx";
import TicketsList from "../screens/ticket/tickets-list.tsx";
import TicketReply from "../screens/ticket/ticket-reply.tsx";
import TransactionsScreen from "../screens/transaction/transactions-screen.tsx";
import PlansList from "../screens/plan/plans-list.tsx";
import NewPlan from "../screens/plan/new-plan.tsx";
import EditPlan from "../screens/plan/edit-plan.tsx";
import GatewaysList from "../screens/getway/gateways-list.tsx";
import NewGateway from "../screens/getway/new-gateway.tsx";
import EditGateway from "../screens/getway/edit-gateway.tsx";
import DiscountsList from "../screens/discount/discounts-list.tsx";
import NewDiscount from "../screens/discount/new-discount.tsx";
import EditDiscount from "../screens/discount/edit-discount.tsx";
import NotificationsScreen from "../screens/admin-pages/notifications-screen.tsx";
import UsersList from "../screens/user/users-list.tsx";
import UserDetail from "../screens/user/user-detail.tsx";
import QuestionsList from "../screens/question/questions-list.tsx";
import NewQuestion from "../screens/question/new-question.tsx";
import EditQuestion from "../screens/question/edit-question.tsx";
import AwardsList from "../screens/award/awards-list.tsx";
import NewAward from "../screens/award/new-award.tsx";
import EditAward from "../screens/award/edit-award.tsx";
import PostsList from "../screens/post/posts-list.tsx";
import NewPost from "../screens/post/new-post.tsx";
import EditPost from "../screens/post/edit-post.tsx";
import ImagesList from "../screens/images/images-list.tsx";
import SettingsScreen from "../screens/settings/settings-screen.tsx";
import AppVersionsList from "../screens/app-version/app-versions-list.tsx";
import NewAppVersion from "../screens/app-version/new-app-version.tsx";
import EditAppVersion from "../screens/app-version/edit-app-version.tsx";

function RequireAuth({ children }: React.PropsWithChildren) {
  const { user } = useContext(AppContext);
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function GuestOnly({ children }: React.PropsWithChildren) {
  const { user } = useContext(AppContext);
  if (user) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
}

export default function AppRoutes() {
  const { user } = useContext(AppContext);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <GuestOnly>
            <LoginScreen />
          </GuestOnly>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminPanelScreen />
          </RequireAuth>
        }
      >
        <Route index element={<MoviesList />} />
        <Route path="movies" element={<MoviesList />} />
        <Route path="movies/genres" element={<GenresScreen />} />
        <Route path="movies/categories" element={<CategoriesScreen />} />
        <Route path="movies/countries" element={<CountriesScreen />} />
        <Route path="movies/languages" element={<LanguagesScreen />} />
        <Route path="movies/networks" element={<NetworksScreen />} />
        <Route path="movies/actors" element={<ActorsScreen />} />
        <Route path="movies/directors" element={<DirectorsScreen />} />
        <Route path="movies/:id" element={<MovieDetail />} />
        <Route path="comments" element={<CommentsScreen />} />
        <Route path="reports" element={<ReportsScreen />} />
        <Route path="tickets" element={<TicketsList />} />
        <Route path="tickets/:id/reply" element={<TicketReply />} />
        <Route path="payments" element={<TransactionsScreen />} />
        <Route path="plans" element={<PlansList />} />
        <Route path="plans/new" element={<NewPlan />} />
        <Route path="plans/:id/edit" element={<EditPlan />} />
        <Route path="gateways" element={<GatewaysList />} />
        <Route path="gateways/new" element={<NewGateway />} />
        <Route path="gateways/:id/edit" element={<EditGateway />} />
        <Route path="discount-codes" element={<DiscountsList />} />
        <Route path="discount-codes/new" element={<NewDiscount />} />
        <Route path="discount-codes/:id/edit" element={<EditDiscount />} />
        <Route path="notifications" element={<NotificationsScreen />} />
        <Route path="users" element={<UsersList />} />
        <Route path="users/:id" element={<UserDetail />} />
        <Route path="questions" element={<QuestionsList />} />
        <Route path="questions/new" element={<NewQuestion />} />
        <Route path="questions/:id/edit" element={<EditQuestion />} />
        <Route path="awards" element={<AwardsList />} />
        <Route path="awards/new" element={<NewAward />} />
        <Route path="awards/:id/edit" element={<EditAward />} />
        <Route path="posts" element={<PostsList />} />
        <Route path="posts/new" element={<NewPost />} />
        <Route path="posts/:id/edit" element={<EditPost />} />
        <Route path="images" element={<ImagesList />} />
        <Route path="settings" element={<SettingsScreen />} />
        <Route path="app-versions" element={<AppVersionsList />} />
        <Route path="app-versions/new" element={<NewAppVersion />} />
        <Route path="app-versions/:id/edit" element={<EditAppVersion />} />
      </Route>
      <Route path="/" element={<Navigate to={user ? "/admin" : "/login"} replace />} />
      <Route path="*" element={<Navigate to={user ? "/admin" : "/login"} replace />} />
    </Routes>
  );
}
